import { useState } from 'react';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@/components';
import { useGetCreateCardQuote } from '@xhype/card-app-sdk';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import ReactJson from 'react-json-view';
import { z } from 'zod';

import { LoaderSkeleton } from './LoaderSkeleton';

/* Schemas */
export const CardCreationQuoteFormSchema = z.object({
  holderId: z
    .string()
    .optional()
    .refine((value) => !value || !isNaN(Number(value)), {
      message: 'Holder Id must be a valid number',
    }),
  tierId: z
    .string()
    .optional()
    .refine((value) => !value || !isNaN(Number(value)), {
      message: 'Tier Id must be a valid number',
    }),
});

/* Types */
export type CardCreationQuoteFormType = z.infer<
  typeof CardCreationQuoteFormSchema
>;

/* Components */
export const CardCreationQuote = () => {
  const [holderId, setHolderId] = useState<number | undefined>(undefined);
  const [tierId, setTierId] = useState<number | undefined>(undefined);

  /* Search Form */
  const searchForm = useForm<CardCreationQuoteFormType>({
    resolver: zodResolver(CardCreationQuoteFormSchema),
  });

  /* Handlers */
  const handleSearch = async (data: CardCreationQuoteFormType) => {
    if (data.tierId) {
      setTierId(Number(data.tierId));
    }

    if (data.holderId) {
      setHolderId(Number(data.holderId));
    }
  };

  /* Render */
  return (
    <section className='w-2/3 flex flex-col items-center justify-center gap-4'>
      <Form {...searchForm}>
        <form
          onSubmit={searchForm.handleSubmit(handleSearch)}
          className='w-full flex flex-col items-center justify-center gap-4'
        >
          <section className='w-full flex flex-row items-center justify-center gap-4'>
            <FormField
              control={searchForm.control}
              name='holderId'
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel>Holder Id</FormLabel>
                  <FormControl>
                    <Input placeholder='1' type='number' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={searchForm.control}
              name='tierId'
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel>Tier Id</FormLabel>
                  <FormControl>
                    <Input placeholder='1' type='number' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </section>
          <Button type='submit' className='cursor-pointer w-full'>
            Get card creation quote
          </Button>
        </form>
      </Form>
      {(!!holderId || !!tierId) && (
        <GetCreationQuote holderId={holderId} tierId={tierId} />
      )}
    </section>
  );
};

/*Internal components */
type GetCreationQuoteProps = {
  holderId?: number;
  tierId?: number;
};

const GetCreationQuote = ({ holderId, tierId }: GetCreationQuoteProps) => {
  const { isLoading, data: creationQuoteData } = useGetCreateCardQuote({
    holderId,
    tierId,
    enabled: !!holderId || !!tierId,
  });

  /* Render */
  return (
    <section className='w-full flex flex-col items-center justify-center'>
      <section className='w-full flex flex-col items-center justify-center gap-4'>
        {!!isLoading && <LoaderSkeleton />}
        {!isLoading && creationQuoteData && (
          <section className='w-full flex flex-col items-start justify-start gap-2'>
            <ReactJson collapsed src={creationQuoteData || {}} />
          </section>
        )}
      </section>
    </section>
  );
};
